import Head from 'next/head'
import Link from 'next/link'
import { GetServerSideProps } from 'next'
import { fetchGames, GamesTree } from '@/lib/rulesApi'
import { DOMAINS, GAME_META, DEFAULT_GAME_META } from '@/lib/games'

type Props = { tree: GamesTree }

export default function RulesHub({ tree }: Props) {
  return (
    <>
      <Head>
        <title>灵缇规则库 · 各类竞赛规则检索中心</title>
        <meta
          name="description"
          content="灵缇规则库：牌类（掼蛋、桥牌、双升…）与球类（匹克球、网球、羽毛球…）竞赛规则的检索、查阅与上传中心。"
        />
      </Head>
      <div className="min-h-screen bg-gray-50">
        <div className="mx-auto max-w-5xl px-4 py-10 md:py-14">
          <header className="mb-12 text-center">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-white px-3 py-1 text-xs font-medium text-gray-600 shadow-sm ring-1 ring-gray-200">
              🏆 竞赛规则一站式检索
            </span>
            <h1 className="mt-4 text-4xl font-black tracking-tight text-gray-900 md:text-5xl">灵缇规则库</h1>
            <p className="mt-3 text-gray-500">牌类 · 球类各项竞赛规则的检索、查阅与上传中心</p>
            <div className="mt-6 flex justify-center gap-3">
              <a
                href="/rules/upload"
                className="rounded-xl bg-brand px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-brand-dark hover:shadow-md"
              >
                上传规则
              </a>
              <a
                href="/rules/mine"
                className="rounded-xl border border-gray-200 bg-white px-5 py-2.5 text-sm font-medium text-gray-600 transition hover:border-gray-300 hover:bg-gray-50"
              >
                我的上传
              </a>
            </div>
          </header>

          {/* 灵缇百科:相关参考(百科亦在一级导航) */}
          <Link
            href="/baike"
            className="mb-12 flex items-center gap-4 rounded-2xl bg-white p-5 shadow-sm ring-1 ring-black/5 transition hover:shadow-md"
          >
            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-brand text-xl text-white">📚</span>
            <span className="min-w-0 flex-1">
              <span className="block font-bold text-gray-900">灵缇百科</span>
              <span className="block truncate text-sm text-gray-500">Matchpoint · 完全/不完全米切尔 · 级差分等赛制术语定义 · 掼蛋名人堂</span>
            </span>
            <span className="shrink-0 text-gray-300">→</span>
          </Link>

          {DOMAINS.map((d) => {
            const node = tree.find((t) => t.domain === d.key)
            const games = node?.games ?? []
            return (
              <section key={d.key} className="mb-12">
                <h2 className="mb-5 flex items-center gap-3 text-lg font-bold text-gray-800">
                  <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-white text-lg shadow-sm ring-1 ring-black/5">
                    {d.emoji}
                  </span>
                  {d.label}
                  <span className="text-sm font-normal text-gray-400">{games.length} 个项目</span>
                  <span className="ml-1 h-px flex-1 bg-gradient-to-r from-gray-200 to-transparent" />
                </h2>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                  {games.map((g) => {
                    const meta = GAME_META[g.game] ?? DEFAULT_GAME_META
                    return (
                      <Link
                        key={g.game}
                        href={`/rules/g/${g.game}`}
                        className="group relative flex items-center gap-4 overflow-hidden rounded-2xl border border-gray-100 bg-white p-4 pl-5 shadow-sm transition hover:-translate-y-0.5 hover:border-transparent hover:shadow-md"
                      >
                        <span className={`absolute inset-y-0 left-0 w-1.5 ${meta.bar}`} />
                        <span
                          className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-xl ${meta.iconBg} ${
                            meta.icon ? 'text-2xl' : 'text-lg font-black'
                          }`}
                        >
                          {meta.icon || g.label.slice(0, 1)}
                        </span>
                        <span className="min-w-0">
                          <div className="text-base font-bold text-gray-900">
                            {g.label}
                          </div>
                          <div className="mt-1">
                            {g.count > 0 ? (
                              <span className="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600">
                                {g.count} 条规则
                              </span>
                            ) : (
                              <span className="text-xs text-gray-400">待补充</span>
                            )}
                          </div>
                        </span>
                        <span className="ml-auto text-gray-300 transition group-hover:translate-x-0.5 group-hover:text-gray-500">
                          →
                        </span>
                      </Link>
                    )
                  })}
                </div>
              </section>
            )
          })}
        </div>
      </div>
    </>
  )
}

export const getServerSideProps: GetServerSideProps<Props> = async () => {
  const tree = await fetchGames()
  return { props: { tree } }
}
